import React, { useEffect, useState } from "react";
import "./joinroompage.css";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setIsRoomHost, setRoomId, setIdentity, setConnectOnlyWithAudio } from "../store/actions";
import JoinRoomTitle from "./JoinRoomTitle";
import JoinRoomContent from "./JoinRoomContent";
import PreviewWindow from "./previewWindow";
import { getRoomExists } from "../utils/api";

const JoinRoompage = (props) => {
  const { setIdentityAction, setRoomIdAction, setIsRoomHostAction, isRoomHost, connectOnlyWithAudio, setConnectOnlyWithAudio } = props;
  const search = useLocation().search;
  const navigate = useNavigate();
  const [full, setFull] = useState(false);
  const [roomError, setRoomError] = useState(null);
  const [joinError, setJoinError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [meetingTiming, setMeetingTiming] = useState(null);
  const [countdown, setCountdown] = useState(5); // ✅ Auto-close countdown

  // ✅ Auto-close countdown when meeting has ended
  useEffect(() => {
    if (!roomError) return;

    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          
          // Check if window was opened programmatically
          if (window.opener) {
            // Window was opened via window.open() - close it
            window.close();
          } else {
            // Window was opened directly - go back in history
            // (better UX than redirecting to login)
            if (window.history.length > 1) {
              window.history.back();
            } else {
              // No history to go back to - redirect to home
              window.location.href = '/';
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [roomError]);

  // Helper function to check if meeting has ended
  const isMeetingEnded = (date, toTime) => {
    if (!date || !toTime) return false;
    const meetingEndDateTime = new Date(`${date}T${toTime}:00`);
    const now = new Date();
    return now > meetingEndDateTime;
  };

  // Helper function to check if meeting hasn't started yet
  const isMeetingNotStarted = (date, fromTime) => {
    if (!date || !fromTime) return false;
    const meetingStartDateTime = new Date(`${date}T${fromTime}:00`);
    const now = new Date();
    const oneMinuteBefore = new Date(meetingStartDateTime.getTime() - 60 * 1000);
    return now < oneMinuteBefore;
  };

  // Helper function to format time remaining or elapsed
  const getTimeStatus = (date, fromTime, toTime) => {
    const now = new Date();
    if (!date || !fromTime || !toTime) return null;

    const startDateTime = new Date(`${date}T${fromTime}:00`);
    const endDateTime = new Date(`${date}T${toTime}:00`);

    if (now > endDateTime) {
      const elapsed = now - endDateTime;
      const hours = Math.floor(elapsed / (1000 * 60 * 60));
      const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `ended ${days} day${days > 1 ? 's' : ''} ago`;
      } else if (hours > 0) {
        return `ended ${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else if (minutes > 0) {
        return `ended ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else {
        return 'ended just now';
      }
    } else if (now < startDateTime) {
      const remaining = startDateTime - now;
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        return `starts in ${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      } else if (minutes > 0) {
        return `starts in ${minutes} minute${minutes > 1 ? 's' : ''}`;
      } else {
        return 'starting soon';
      }
    } else {
      return 'in progress';
    }
  };

  useEffect(() => {
    const urlparams = new URLSearchParams(search);
    const roomId = urlparams.get("roomId");
    const userName = urlparams.get('name');
    const fromTime = urlparams.get('fromTime');
    const toTime = urlparams.get('toTime');
    const date = urlparams.get('date');

    if (roomId && userName) {
      console.log("🔗 Meeting link detected");

      if (date && toTime && isMeetingEnded(date, toTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        console.error("❌ Meeting has ended");
        setRoomError(`This meeting ${status}. The meeting was scheduled for ${fromTime} - ${toTime} on ${date}.`);
        return;
      }

      if (date && fromTime && isMeetingNotStarted(date, fromTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        setJoinError(`This meeting ${status}. You can join up to 1 minute before the start time.`);
      }

      setIsRoomHostAction(false);
      setRoomIdAction(roomId);
      setIdentityAction(userName);

      if (fromTime || toTime) {
        setMeetingTiming({ fromTime, toTime, date });
      }

      const checkRoom = async () => {
        try {
          const response = await getRoomExists(roomId);
          const { roomExists, full } = response;

          if (full) {
            setFull(true);
            setRoomError("Meeting is full. Please try again later.");
            return;
          }

          console.log("✅ Room check passed - ready to show preview");
        } catch (error) {
          console.error("❌ Error checking room:", error);
          setRoomError("Error checking meeting. Please try again.");
        }
      };

      checkRoom();
    } else if (roomId) {
      setIsRoomHostAction(false);
      setRoomIdAction(roomId);

      const fromTime = urlparams.get('fromTime');
      const toTime = urlparams.get('toTime');
      const date = urlparams.get('date');

      if (date && toTime && isMeetingEnded(date, toTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        setRoomError(`This meeting ${status}. The meeting was scheduled for ${fromTime} - ${toTime} on ${date}.`);
        return;
      }

      if (date && fromTime && isMeetingNotStarted(date, fromTime)) {
        const status = getTimeStatus(date, fromTime, toTime);
        setJoinError(`This meeting ${status}. You can join up to 1 minute before the start time.`);
      }

      if (fromTime || toTime) {
        setMeetingTiming({ fromTime, toTime, date });
      }
    }
  }, [search, setIsRoomHostAction, setRoomIdAction, setIdentityAction]);

  const urlparams = new URLSearchParams(search);
  const autoRoomId = urlparams.get("roomId") || "";
  const autoName = urlparams.get("name") || "";
  const urlFromTime = urlparams.get("fromTime");
  const urlToTime = urlparams.get("toTime");
  const urlDate = urlparams.get("date");

  const handleShowPreview = (show, timing) => {
    setShowPreview(show);
    if (timing) {
      setMeetingTiming(timing);
    } else if (show && (urlFromTime || urlToTime)) {
      setMeetingTiming({ fromTime: urlFromTime, toTime: urlToTime, date: urlDate });
    } else if (!show) {
      setMeetingTiming(null);
    }
  };

  const handlePreviewJoin = () => {
    localStorage.setItem('participantsEnabled', true);
    const params = new URLSearchParams();
    if (urlFromTime) params.append('fromTime', urlFromTime);
    if (urlToTime) params.append('toTime', urlToTime);
    if (urlDate) params.append('date', urlDate);

    const queryString = params.toString();
    navigate(`/room${queryString ? `?${queryString}` : ''}`);
  };

  const handlePreviewCancel = () => {
    setShowPreview(false);
    setMeetingTiming(null);
    window.close();
  };

  //  Show meeting ended / error state with auto-close countdown
if (roomError) {
  return (
    <div className="join_room_page_container">
      <div className="join_room_page_panel">
        <JoinRoomTitle isRoomHost={isRoomHost} />
        <div className="error-content">
          <div className="meeting_ended_message">  {/* ← changed */}
            <p className="error_message_paragraph">{roomError}</p>
            <p className="meeting_ended_countdown">  {/* ← changed */}
              This window will close in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

  return (
    <>
      <div className="join_room_page_container">
        <div className="join_room_page_panel">
          <JoinRoomTitle isRoomHost={isRoomHost} />
          <JoinRoomContent
            autoRoomId={autoRoomId}
            autoName={autoName}
            onShowPreview={handleShowPreview}
          />
        </div>
      </div>

      {showPreview && (
        <PreviewWindow
          onJoin={handlePreviewJoin}
          onCancel={handlePreviewCancel}
          connectOnlyWithAudio={connectOnlyWithAudio}
          setConnectOnlyWithAudio={setConnectOnlyWithAudio}
          meetingTiming={meetingTiming}
          joinError={joinError}
        />
      )}
    </>
  );
};

const mapStoreStateToProps = (state) => {
  return {
    ...state,
  };
};

const mapActionsToProps = (dispatch) => {
  return {
    setIsRoomHostAction: (isRoomHost) => dispatch(setIsRoomHost(isRoomHost)),
    setRoomIdAction: (roomId) => dispatch(setRoomId(roomId)),
    setIdentityAction: (identity) => dispatch(setIdentity(identity)),
    setConnectOnlyWithAudio: (onlyWithAudio) => dispatch(setConnectOnlyWithAudio(onlyWithAudio)),
  };
};

export default connect(mapStoreStateToProps, mapActionsToProps)(JoinRoompage);